'use client'

import { useState } from 'react'
import { usePathname } from 'next/navigation'
import { ChevronRight } from 'lucide-react'
import { navigateUrl } from '@/utils/client-request'
import { formatImageURL } from '@/utils/util'
import { hashIndex, getInitials } from '@/utils/avatar'

const SOFT_PALETTE: { bg: string, fg: string }[] = [
  { bg: '#E8E2FF', fg: '#1a1130' },
  { bg: '#FFD9E7', fg: '#1a1130' },
  { bg: '#FFE0C9', fg: '#1a1130' },
  { bg: '#DCEEF6', fg: '#0B3D5C' },
  { bg: '#FEF08A', fg: '#854D0E' },
  { bg: '#CFE9D7', fg: '#0F3D2B' },
  { bg: '#F5EFE6', fg: '#7A5C2E' },
  { bg: '#FCE7F3', fg: '#9D174D' },
  { bg: '#E9F4D8', fg: '#365314' },
  { bg: '#BAE6FD', fg: '#0C4A6E' },
  { bg: '#F1EEE7', fg: '#2A2418' },
  { bg: '#FFF1E6', fg: '#A04A1F' },
]

export default function BrandCard({ brand }: { brand: any, index?: number }) {
  const pathname = usePathname() || ''
  const [hover, setHover] = useState(false)
  const seed = String(brand?.id || brand?.name || '')
  const { bg: tileBg, fg: tileFg } = SOFT_PALETTE[hashIndex(seed, SOFT_PALETTE.length)]
  const initials = getInitials(brand?.name)
  const count = brand?.productCount || 0

  return (
    <a
      href={navigateUrl(`/product?brand=${brand.id}`, pathname)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="group flex flex-col gap-3 outline-none"
    >
      <div
        className="relative aspect-square rounded-2xl overflow-hidden transition-all"
        style={{ background: tileBg, boxShadow: hover ? '0 10px 30px -10px rgb(0 0 0 / 0.18)' : '0 1px 2px 0 rgb(0 0 0 / 0.05)' }}
      >
        <div className="absolute inset-0 transition-opacity" style={{ background: 'radial-gradient(120% 80% at 50% 30%, rgba(255,255,255,0.18), transparent 60%)', opacity: hover ? 1 : 0 }} />

        <div className="absolute inset-0 flex items-center justify-center p-6 text-center">
          {brand?.thumbnail ? (
            <img src={formatImageURL(brand.thumbnail)} alt={brand.name} className="max-w-[70%] max-h-[70%] object-contain" />
          ) : (
            <span className="font-bold font-gilroy leading-none" style={{ color: tileFg, fontSize: 'clamp(2rem, 5vw, 3.25rem)', letterSpacing: '-0.04em' }}>
              {initials}
            </span>
          )}
        </div>

        <div
          className="absolute left-0 right-0 bottom-0 px-3 pb-3 pt-8 transition-transform"
          style={{ transform: hover ? 'translateY(0)' : 'translateY(110%)', background: 'linear-gradient(to top, rgba(0,0,0,0.45), transparent)' }}
        >
          <div className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white text-foreground text-[12px] font-semibold shadow-sm">
            Брэнд үзэх <ChevronRight className="size-3.5" />
          </div>
        </div>
      </div>

      <div className="px-0.5">
        <div className="text-[14px] font-semibold leading-tight truncate text-foreground">{brand.name}</div>
        <div className="mt-0.5 text-[12px] text-foreground/55 leading-tight">
          <span className="font-semibold text-foreground">{count.toLocaleString('en-US')}</span> бараа
        </div>
      </div>
    </a>
  )
}
